import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Radiobutton } from '@vinnova/ui';
import {
  map,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { FormQuestionFollowUp } from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import { QuestionStructureOptions } from '../../../core/models/question/question-options.model';
import { Question } from '../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { QuestionNumberService } from '../../../core/services/question-number.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';
import { PreviousAnswersService } from '../../../core/services/previous-answers.service';

@UntilDestroy()
@Component({
  selector: 'lib-question-radiobutton',
  templateUrl: './question-radiobutton.component.html',
  styleUrls: ['./question-radiobutton.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuestionRadiobuttonComponent implements OnInit, OnChanges {
  value: FormControl<string>;
  checkedValue: string;
  readOnly$: Observable<ReadonlyState>;
  questionChange = new Subject<void>();
  questionChange$ = this.questionChange.asObservable();
  questionNumber$: Observable<unknown>;
  name: string;
  activeLang$: Observable<string>;
  errorMessage$: Observable<string>;
  radiobuttonObjects: WritableSignal<Radiobutton[]> = signal(undefined);
  readOnly: WritableSignal<string> = signal(undefined);
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;
  labelPrefixForRadiobutton = signal(undefined);
  showChanges = input<boolean>();
  readonly previousAnswersService = inject(PreviousAnswersService);

  ReadonlyState = ReadonlyState;

  @Input() labelPrefix: string;
  @Input() repeaterGroupId: string;

  _questionForm: FormGroup<ControlsOf<FormQuestionFollowUp>>;
  @Input()
  set questionForm(form: FormGroup<ControlsOf<FormQuestionFollowUp>>) {
    this._questionForm = form;
    this.value = this._questionForm.get('value');
    this.checkedValue = this.value.value ?? '';
    this.value.touch$.pipe(take(1)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }
  get questionForm() {
    return this._questionForm;
  }
  _question: Question;
  @Input()
  set question(value: Question) {
    this._question = value;
    this.questionChange.next();
    this.name = this.labelPrefix + this._question.id;
  }

  get question() {
    return this._question;
  }

  get structure() {
    return this.question.structure as QuestionStructureOptions;
  }

  constructor(
    private formValidationService: FormValidationService,
    private cdr: ChangeDetectorRef,
    private translatePipe: TranslatePipe,
    private translocoService: TranslocoService,
    private questionNumberService: QuestionNumberService,
    private questionValueChangeService: QuestionValueChangeService,
    private formsQuery: RendererFormsQuery,
  ) {}

  ngOnInit() {
    this.readOnly$ = this.formsQuery.selectReadOnly();
    this.activeLang$ = this.translocoService.langChanges$;

    merge(this.questionChange$, this.translocoService.langChanges$)
      .pipe(
        map(() => {
          return (
            this.question.structure as QuestionStructureOptions
          ).options.map((option) => {
            return {
              id: option.value,
              label: this.translatePipe.transform(
                this.question.translations?.list[option.value],
                this.translocoService.getActiveLang(),
                option.text,
              ),
            };
          });
        }),
        untilDestroyed(this),
      )
      .subscribe((objects) => {
        this.radiobuttonObjects.set(objects);
        this.setReadonly();
      });

    // populate initial answers from the db
    this.questionValueChangeService.patchInitialValue(
      this.question.id,
      this.repeaterGroupId,
      this.value,
      this.question,
    );

    this.questionNumber$ = this.questionNumberService.getQuestionNumberForLabel(
      this.question.id,
    );

    this.setErrorMessage();

    this.readOnly$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.setReadonly());

    this.labelPrefixForRadiobutton.set(
      this.labelPrefix
        ? this.labelPrefix + this.question.id.split('-')[0] + '-'
        : this.question.id.split('-')[0] + '-',
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }

      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.value,
          {
            questionId: this.question.id,
            repeaterGroupId: this.repeaterGroupId,
          },
          this.question,
        )
        .pipe(untilDestroyed(this))
        .subscribe();

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(this.question, this.repeaterGroupId, this.value)
        .pipe(untilDestroyed(this))
        .subscribe(() => this.setReadonly());

      if (
        this.formsQuery.getReadOnly() &&
        !changes['repeaterGroupId'].firstChange
      ) {
        this.setReadonly();
      }
    }
  }

  setErrorMessage() {
    this.errorMessage$ = this.readOnly$.pipe(
      switchMap((readonly) => {
        switch (readonly) {
          case ReadonlyState.Editable: {
            return this.formValidationService
              .questionInvalid(this.value, this.question.id)
              .pipe(
                switchMap((invalid) => {
                  return invalid
                    ? this.translocoService.selectTranslate(
                        'radiobuttons_required',
                      )
                    : of(undefined);
                }),
              );
          }
          case ReadonlyState.Locked: {
            return this.errorMessage(this.value);
          }
          case ReadonlyState.Reader: {
            return of(undefined);
          }
        }
      }),
    );
  }

  errorMessage(formControl: FormControl<string>): Observable<string> {
    if (formControl.errors?.required) {
      return this.translocoService.selectTranslate('field_required');
    }
    return of(undefined);
  }

  private getChoosenRadio(radiobuttons: Radiobutton[]): string {
    for (const radiobutton of radiobuttons) {
      if (radiobutton.id === this.value.value) {
        return radiobutton.label;
      }
    }
  }

  private setReadonly() {
    if (this.formsQuery.getReadOnly()) {
      this.readOnly.set(this.getChoosenRadio(this.radiobuttonObjects()));
    }
  }
}
